/**
 * Catalog components — the parts of the redesign the comp does not draw.
 *
 * The Claude Design comps cover the homepage, Service and About. `/bikes` and
 * `/bikes/:handle` have no comp, so these are built from the comp's own
 * vocabulary — the same type scale, the same card, the same button — rather
 * than invented. There is no pixel diff to check them against, which is exactly
 * why they reuse existing pieces instead of introducing new ones.
 *
 * The availability rules here are settled by the redesign spec and are not
 * preferences:
 *
 *  - **Badge only the exceptions.** A bike that is simply in stock gets NO
 *    badge. Badging every card turns a healthy floor into a wall of labels.
 *  - **When a bike is gone the CTA is REPLACED by a real contact action.**
 *    Never a disabled button wearing a badge.
 */

import { useState, type CSSProperties } from "react";
import { A, Button, H2 } from "./primitives";
import { color, font, radius, space } from "@/lib/design-tokens";
import { shop } from "@/lib/copy/shop";
import { bikes as copy } from "@/lib/copy/bikes";
import type { Bike } from "@/types/bike";
import { photosFor } from "@/lib/photos";
import { productName } from "@/lib/product_name";
import { availableColors } from "@/lib/variant_inventory";

const money = (n: number) => "$" + Math.round(n).toLocaleString("en-US");

export interface CatalogBrand {
  slug: string;
  name: string;
}

function brandHref(currentBrands: string[], slug: string) {
  const next = currentBrands.includes(slug)
    ? currentBrands.filter((brand) => brand !== slug)
    : [...currentBrands, slug];
  const query = next.map((brand) => `brand[]=${encodeURIComponent(brand)}`).join("&");
  return query ? `/bikes?${query}` : "/bikes";
}

/** The catalog's only facet: links backed by the real brand field. */
export function BrandFilter({
  brands,
  currentBrands,
}: {
  brands: CatalogBrand[];
  currentBrands: string[];
}) {
  const chipStyle = (selected: boolean): CSSProperties => ({
    display: "inline-block",
    padding: "9px 14px",
    border: `1px solid ${selected ? color.blue : color.border}`,
    borderRadius: radius.chip,
    background: selected ? color.blue : color.white,
    color: selected ? color.white : color.ink,
    fontFamily: font.mono,
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: ".04em",
    textDecoration: "none",
  });

  return (
    <nav
      aria-label="Filter bikes by brand"
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: `36px ${space.gutter} 42px`,
        position: "sticky",
        top: 0,
        zIndex: 20,
        background: color.white,
        boxShadow: `0 1px 0 ${color.border}`,
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
        <A
          href="/bikes"
          aria-label={currentBrands.length === 0 ? "All brands, selected" : "Clear all brand filters"}
          data-selected={currentBrands.length === 0 || undefined}
          style={chipStyle(currentBrands.length === 0)}
        >
          {currentBrands.length === 0 ? "All brands" : "Clear filters"}
        </A>
        {brands.map((brand) => {
          const selected = currentBrands.includes(brand.slug);
          return (
            <A
              key={brand.slug}
              href={brandHref(currentBrands, brand.slug)}
              aria-label={selected ? `${brand.name}, selected` : `Filter by ${brand.name}`}
              data-selected={selected || undefined}
              style={chipStyle(selected)}
            >
              {brand.name}
            </A>
          );
        })}
      </div>
    </nav>
  );
}

/**
 * Renders NOTHING for a bike that is in stock. That is the whole point: the
 * badge marks the exception, so the buyable subset stays legible at a glance.
 */
export function AvailabilityBadge({ bike }: { bike: Bike }) {
  if (bike.availability_state === "available") return null;
  return (
    <span
      style={{
        position: "absolute",
        top: "10px",
        left: "10px",
        fontFamily: font.sans,
        fontSize: "13px",
        fontWeight: 600,
        color: color.white,
        background: color.soldRed,
        borderRadius: radius.chip,
        padding: "4px 8px",
      }}
    >
      {bike.availability_state === "past_model" ? "Past model" : copy.soldBadge}
    </span>
  );
}

/**
 * The one conversion component.
 *
 * `/book` owns the live scheduler and its human contact fallback. This CTA only
 * leads there; it does not claim a slot is held. When the bike is gone the
 * button is replaced by a different real action, and the phone number remains
 * available in every stock state.
 */
export function VisitCta({ bike }: { bike: Bike }) {
  return (
    <div style={{ marginTop: "26px" }}>
      {bike.availability_state === "available" ? (
        <Button href={`/book?bike=${encodeURIComponent(bike.handle)}`} variant="red" style={{ fontSize: "16px", padding: "15px 26px" }}>
          {copy.askAboutTestRide}
        </Button>
      ) : (
        <Button href="/contact?about=sales" variant="blue" style={{ fontSize: "16px", padding: "15px 26px" }}>
          {copy.askWhatsComing}
        </Button>
      )}
      <div style={{ marginTop: "12px", fontSize: "15px", color: color.slate }}>
        {copy.questionsPrefix}
        <a href={shop.phoneHref} className="bb-underline-grow" style={{ fontWeight: 700 }}>
          {shop.phone}
        </a>
      </div>
    </div>
  );
}

/**
 * A catalog tile. Same shape as the homepage card, plus the badge.
 *
 * The real photograph goes where the placeholder hatch is now. `image_url` is
 * used when the catalog has one; the hatch is the fallback, matching the comp's
 * treatment, so the page reads correctly before the shoot lands.
 */
function CatalogCard({ bike }: { bike: Bike }) {
  const photos = photosFor(bike.handle);
  const colorsAvailable = availableColors(bike);
  const imageCandidates = [
    photos.studio,
    photos.lifestyleHero,
    bike.image_url ? {
      url: bike.image_url,
      altText: bike.image_alt,
      width: bike.image_width,
      height: bike.image_height,
      fit: "contain" as const,
      ground: color.white,
      srcSet: undefined,
      focalPosition: "center",
    } : undefined,
  ].filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate?.url));
  const [imageIndex, setImageIndex] = useState(0);
  const image = imageCandidates[imageIndex];
  const hasImage = Boolean(image);
  const imageGround = image?.ground;
  const hasListedPrice = bike.availability_state !== "past_model" && Number.isFinite(bike.price) && bike.price > 0;
  return (
    <div data-reveal="rise" data-reveal-stagger="">
      <A
        href={`/bikes/${bike.handle}`}
        className="bb-bike-card"
        data-asset-ground={hasImage && imageGround ? "" : undefined}
        style={{
          display: "block",
          position: "relative",
          aspectRatio: "4/3",
          borderRadius: radius.card,
          overflow: "hidden",
          // Studio shots ship on white, and several pages put these cards on a
          // white section — without a boundary the availability badge reads as
          // floating in the void (Aaron's 2026-09-07 screenshot). The hairline
          // makes card, photo, and badge one object in every ground.
          border: `1px solid ${color.borderCard}`,
          // The hatch is the PLACEHOLDER, so it only shows when there is no
          // photograph. A product shot is transparent PNG on white; letting the
          // hatch show through around it reads as a rendering bug rather than a
          // texture.
          // The ground is measured from the photograph's own corners at ingest,
          // so the card and the photo share one background and there is no seam
          // between them. Null (transparent art, or Benno's lit gradient) falls
          // back to white.
          background: hasImage
            ? imageGround || color.white
            : `repeating-linear-gradient(45deg,${color.surface},${color.surface} 12px,${color.hatchLight} 12px,${color.hatchLight} 24px)`,
        }}
      >
        {hasImage ? (
          <img
            data-asset-ground={imageGround ? "" : undefined}
            src={image.url}
            srcSet={image.srcSet}
            sizes="(max-width: 700px) 92vw, 360px"
            alt={image.altText || productName(bike)}
            width={image.width || undefined}
            height={image.height || undefined}
            loading="lazy"
            onError={() => setImageIndex((current) => current + 1)}
            style={{ width: "100%", height: "100%", objectFit: image.fit || "contain", objectPosition: image.focalPosition || "center", display: "block" }}
          />
        ) : (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "flex-end", padding: "12px" }}>
            <span
              style={{
                fontFamily: font.mono,
                fontSize: "11px",
                color: color.slate,
                background: "rgba(255,255,255,.8)",
                padding: "3px 7px",
                borderRadius: radius.chip,
              }}
            >
              photo — side profile, {bike.title}
            </span>
          </div>
        )}
        <AvailabilityBadge bike={bike} />
      </A>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: "12px",
          marginTop: "8px",
        }}
      >
        <div style={{ fontWeight: 700, fontSize: "19px", minWidth: 0, flex: "1 1 180px" }}>
          <A
            href={`/bikes/${bike.handle}`}
            className="bb-bike-title"
            style={{ color: color.ink, display: "inline-block", padding: "6px 0" }}
          >
            {bike.title}
          </A>
        </div>
        {hasListedPrice && (
          <div style={{ fontSize: "17px", color: color.inkMuted, flex: "none" }}>{money(bike.price)}</div>
        )}
      </div>
      <div style={{ fontSize: "14px", color: color.slate }}>{bike.brand_display}</div>
      {colorsAvailable.length > 0 && (
        <div style={{ marginTop: "3px", fontSize: "14px", color: color.slate }}>
          {colorsAvailable.length === 1 ? "Available color" : "Available colors"}: {colorsAvailable.join(", ")}
        </div>
      )}
      {bike.availability_state === "check_availability" && (
        <div style={{ marginTop: "4px", fontSize: "14px", color: color.slate }}>
          {copy.soldPrefix}
          <A href="/contact?about=sales" className="bb-underline-grow" style={{ fontWeight: 700 }}>
            {copy.soldLink}
          </A>
        </div>
      )}
    </div>
  );
}

/**
 * One labeled section of bikes.
 */
export function BikeSection({
  id,
  heading,
  blurb,
  bikes,
  style,
  brandSlug,
}: {
  id: string;
  heading: string;
  blurb?: string;
  bikes: Bike[];
  style?: CSSProperties;
  brandSlug?: string;
}) {
  if (!bikes.length) return null;
  return (
    <section
      id={id}
      data-section={id}
      data-brand-group={brandSlug}
      style={{ maxWidth: "1200px", margin: "0 auto", padding: `0 ${space.gutter} ${space.sectionY}`, ...style }}
    >
      <H2 data-reveal>{heading}</H2>
      {blurb && (
        <div style={{ margin: "10px 0 36px", fontSize: "15px", color: color.slate }}>{blurb}</div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(min(280px,100%),1fr))",
          gap: `${space.gridRow} ${space.gridCol}`,
          marginTop: blurb ? 0 : "28px",
        }}
      >
        {bikes.map((b) => (
          <CatalogCard key={b.handle} bike={b} />
        ))}
      </div>
    </section>
  );
}
